<template>
  <v-sheet color="white" class="rounded-lg shadow">
    <v-container fluid fill-width class="ma-0 pa-0">
      <v-row class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 pa-0">
          <v-sheet color="white" class="rounded-lg shadow">
            <v-container v-if="showTitleBar" fluid fill-width class="ma-0 pa-0">
              <v-row class="ma-0 pa-0">
                <v-col cols="12" class="ma-0 pa-0">
                  <v-card color="#eeeeee" class="pt-1">
                    <v-card-title class="primary--text pl-3 py-2"
                      >{{ title }} &nbsp;

                      <v-tooltip v-if="anovaPvalsInput" top color="primary">
                        <template v-slot:activator="{ on: tooltip }">
                          <span v-on="{ ...tooltip }">{{
                            '(' + anovaPvalsInput.numGroups + ')'
                          }}</span>
                        </template>
                        <span
                          >Statistical analysis was performed with
                          {{ anovaPvalsInput.numGroups }} study group(s).</span
                        >
                      </v-tooltip>
                      <v-spacer />
                      <span v-if="expanded">
                        <v-chip
                          v-for="(x, index) in pval_thresholds"
                          :key="`vc1-${index}`"
                          :color="
                            colors['pvals'][x + '-' + colorScheme]['color']
                          "
                          :class="
                            colorScheme == 'brewer5' && x == '0.001'
                              ? 'white--text'
                              : ''
                          "
                          >p &lt; {{ x }}</v-chip
                        ></span
                      >
                      <v-spacer v-if="expanded" />
                      <v-toolbar-items v-if="showTitleBar">
                        <v-icon v-if="expanded" @click="expandClicked(false)"
                          >chevron_left</v-icon
                        >
                        <v-icon v-else @click="expandClicked(true)"
                          >chevron_right</v-icon
                        >
                      </v-toolbar-items>
                    </v-card-title>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-sheet>
        </v-col>
      </v-row>

      <v-row v-if="!showTitleBar" class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 pa-0" align="center">
          <v-sheet color="white" class="rounded-lg shadow">
            <div class="pa-1 d-flex align-center justify-center">
              <v-tooltip
                v-for="(pv, index) in pval_thresholds"
                :key="`pvt-vc-${index}`"
                bottom
                color="primary"
              >
                <template v-slot:activator="{ on: tooltip }">
                  <span
                    :style="
                      pval_threshold == pv
                        ? 'padding: 0px 0px;'
                        : 'padding: 0px 1px;'
                    "
                    v-on="{ ...tooltip }"
                  >
                    <span
                      v-if="pval_threshold == pv"
                      class="pa-0 ma-0 pl-1"
                      style="color: red; font-size: 3rem; vertical-align: middle;"
                      >*</span
                    ><span
                      v-else
                      class="pa-0 ma-0 pl-1"
                      style="color: white; font-size: 3rem; vertical-align: middle;"
                      >*</span
                    >
                    <v-chip
                      :key="`pv-vc-${index}`"
                      :color="getPvalColor(pv)"
                      :class="
                        (colorScheme == 'brewer5' && pv == '0.001'
                          ? 'white--text'
                          : '') + ' px-2 mx-1'
                      "
                      :style="
                        pval_threshold == pv
                          ? 'border: 3px solid red;'
                          : 'border: 2px solid black;'
                      "
                      @click="setPvalThreshold(pv)"
                    >
                      p &lt; {{ pv }}</v-chip
                    ></span
                  ></template
                >
                <span
                  >{{
                    pval_threshold == pv ? 'Highlighting' : 'Click to highlight'
                  }}
                  variables with p-value &lt; {{ pv }}</span
                >
              </v-tooltip>
            </div>
          </v-sheet>
        </v-col>
      </v-row>

      <v-row class="pa-0 mt-0">
        <v-col class="pa-1 px-2" cols="12">
          <v-data-table
            :key="'dtr-c' + analysis.collection.id + '-a' + analysis.index"
            ref="ovars_table"
            v-model="selectedVars"
            class="pt-0 mt-0"
            dense
            :show-select="multiSelect"
            :headers="headers"
            :items="tableRows"
            item-key="id"
            hide-footer
            :sort-by="['p_value', 'label']"
            @current-items="tableRowsUpdated"
          >
            <template v-slot:item="props">
              <tr
                :key="
                  'dtr-c' +
                    analysis.collection.id +
                    '-a' +
                    analysis.index +
                    '-r' +
                    props.item.id
                "
                :class="{ selectedRow: isOVSelected(props.item) }"
                @click="table_row_click(props.item)"
              >
                <td
                  v-if="multiSelect"
                  class="pa-0 ma-0"
                  justify="center"
                  align="center"
                >
                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <v-simple-checkbox
                        :value="props.isSelected"
                        class="pa-0 ma-0"
                        dense
                        hide-details
                        v-on="{ ...tooltip }"
                        @input="props.select($event)"
                      />
                    </template>
                    <span>{{ checkboxTooltip }}</span>
                  </v-tooltip>
                </td>

                <td
                  class="text-subtitle-1 text-xs-left"
                  :style="
                    expanded || !colorScaleWhenMinimized
                      ? ''
                      : {
                          backgroundColor: pval_table_cell_color(props.item),
                          color: pval_table_text_color(props.item),
                        }
                  "
                >
                  <v-row align="center" class="rowElement">
                    <v-tooltip v-if="showCategoryIcons" bottom color="primary">
                      <template v-slot:activator="{ on: tooltip }">
                        <img
                          :src="
                            '/images/' + props.item.category + '-icon-128.png'
                          "
                          :title="props.item.category"
                          style="height:2em"
                          class="ma-1"
                          @error="missingDomainFallback"
                          v-on="{ ...tooltip }"
                        />
                      </template>
                      <span>{{ props.item.category }}</span>
                    </v-tooltip>
                    <v-tooltip v-if="!expanded" bottom color="primary">
                      <template v-slot:activator="{ on: tooltip }">
                        <span v-on="{ ...tooltip }">
                          {{ props.item.abbreviation }}
                        </span>
                      </template>
                      <span
                        >{{
                          props.item.label +
                            '  Test: ' +
                            '1-way ANOVA' +
                            '  F-Statistic:' +
                            format_fval(variable_fval(props.item)) +
                            '  p-Value:' +
                            format_pval(variable_pval(props.item))
                        }}
                      </span>
                    </v-tooltip>

                    <v-tooltip v-else top color="primary">
                      <template v-slot:activator="{ on: tooltip }">
                        <span v-on="{ ...tooltip }">
                          {{ props.item.abbreviation }}
                        </span>
                      </template>
                      <span>{{ props.item.label }}</span>
                    </v-tooltip>
                  </v-row>
                </td>

                <td v-if="expanded" class="text-subtitle-1 text-xs-left">
                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">{{
                        variable_test_abbrev(props.item)
                      }}</span>
                    </template>
                    <span>{{ variable_test_name(props.item) }}</span>
                  </v-tooltip>
                </td>

                <td v-if="expanded" class="text-subtitle-1 text-xs-left">
                  {{ format_fval(variable_fval(props.item)) }}
                </td>

                <td
                  v-if="expanded"
                  :key="props.item.id"
                  class="text-subtitle-1 text-xs-left"
                  :style="{
                    backgroundColor: pval_table_cell_color(props.item),
                    color: pval_table_text_color(props.item),
                  }"
                >
                  {{ format_pval(variable_pval(props.item)) }}
                </td>
                <td class="pa-0 ma-0" style="width: 0.5em;">
                  <span
                    v-if="variable_pval(props.item) < pval_threshold"
                    class="pa-0 ma-0 pl-1"
                    style="color: red; line-height: 0; font-size: 1.7rem;"
                    >*</span
                  >
                </td>
                <td class="pa-1 ma-0 px-2" style="width: 0.5em;">
                  <v-tooltip v-if="props.item.error" bottom color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <v-icon
                        v-if="props.item.error"
                        color="warning"
                        medium
                        class="pa-0 ma-0"
                        v-on="{ ...tooltip }"
                        >warning</v-icon
                      >
                    </template>
                    <span>{{ props.item.error }}</span>
                  </v-tooltip>
                </td>
                <td>
                  <v-icon v-if="isOVSelected(props.item)" medium
                    >arrow_right_alt</v-icon
                  >
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </v-sheet>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { state, actions } from '@/store/modules/dataExplorer/types';
import { format } from 'd3-format';
import { colors } from '@/utils/colors';
import { missingDomainFallback } from '@/utils/helpers';

export default {
  components: {},
  props: {
    analysis: {
      type: Object,
      required: true,
      default: () => {},
    },
    anovaPvalsInput: {
      type: Object,
      required: true,
    },
    anovaPvals: {
      type: Array,
      required: false,
      default: () => [],
    },
    outcomeVariables: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      required: false,
      default: 'Analytics',
    },
    showTitleBar: {
      type: Boolean,
      required: false,
      default: true,
    },
    selectedVariables: {
      type: Array,
      required: false,
      default: () => [],
    },
    autoselectFirstVariable: {
      type: Boolean,
      required: false,
      default: false,
    },
    multiSelect: {
      type: Boolean,
      required: false,
      default: false,
    },
    checkboxTooltip: {
      type: String,
      required: false,
      default: 'Check to select this outcome variable.',
    },
    showCategoryIcons: {
      type: Boolean,
      required: false,
      default: true,
    },
    expanded: {
      type: Boolean,
      required: false,
      default: true,
    },
    colorScaleWhenMinimized: {
      type: Boolean,
      required: false,
      default: true,
    },
    colorScheme: {
      type: String,
      required: false,
      default: 'val100',
    },
  },
  data() {
    return {
      tableRows: [],
      sortedTableRows: [],
      selectedVars: [],
      colors: colors,
      pval_dict: {},
      autoselectPending: false,
      pval_thresholds: [1.0, 0.1, 0.05, 0.01, 0.001],
      missingDomainFallback: missingDomainFallback,
    };
  },
  computed: {
    ...mapState('dataExplorer', {
      pval_threshold: state.PVAL_THRESHOLD,
    }),
    headers() {
      var headers = [
        {
          text: 'Variable',
          value: 'label',
          class: 'text-subtitle-1 font-weight-bold variable-header',
        },
      ];
      if (this.expanded) {
        headers.push({
          text: 'Test',
          value: 'label',
          sortable: false,
          class: 'text-subtitle-1 font-weight-bold variable-header',
        });
        headers.push({
          text: 'F-Statistic',
          value: 'f_statistic',
          sortable: true,
          class: 'text-subtitle-1 font-weight-bold variable-header',
        });
        headers.push({
          text: 'p-Value',
          value: 'p_value',
          sortable: true,
          class: 'text-subtitle-1 font-weight-bold variable-header',
        });
      }
      // significant p-value asterisk
      headers.push({
        text: '',
        sortable: false,
      });
      // warning symbol
      headers.push({
        text: '',
        sortable: false,
      });
      // extra column to display right-facing arrow/chevron
      headers.push({
        text: '',
        value: '',
        sortable: false,
        class: 'text-subtitle-1 font-weight-bold variable-header',
      });
      return headers;
    },
  },
  watch: {
    anovaPvals() {
      this.update_table_rows();
    },
    outcomeVariables() {
      //this.update_table_rows();
    },
    selectedVariables(sv) {
      // modify selectedVars if not the same
      var current = this.selectedVars
        .map(v => v.var.label)
        .sort()
        .join(',');
      var sel = sv
        .map(v => v.label)
        .sort()
        .join(',');
      if (sel != current) {
        var selIds = {};
        sv.map(v => {
          selIds[v.id] = 1;
        });
        this.selectedVars = this.tableRows.filter(tr => tr.id in selIds);
      }
    },
    selectedVars(sv) {
      this.emitSortedSelectedVars(sv);
    },
  },
  mounted() {
    this.update_table_rows();
  },
  methods: {
    ...mapActions('dataExplorer', {
      setPvalThreshold: actions.SET_PVAL_THRESHOLD,
    }),
    format_fval(fv) {
      if (fv == null) {
        return '-';
      }
      return `${format('.2f')(fv)}`;
    },
    format_pval(pval) {
      if (pval == null) {
        return '-';
      }
      if (pval < 0.001) {
        return '<0.001';
      }
      return `${format('.3f')(pval)}`;
    },
    variable_fval(ov) {
      const pd = this.pval_dict;
      if (ov.label in pd) {
        const { fval } = pd[ov.label];
        return fval;
      }
      return null;
    },
    variable_pval(ov) {
      const pd = this.pval_dict;
      if (ov.label in pd) {
        const { pval } = pd[ov.label];
        return pval;
      }
      return null;
    },
    variable_test_abbrev(ov) {
      const pd = this.pval_dict;
      if (ov.label in pd) {
        const { test_abbrev } = pd[ov.label];
        return test_abbrev;
      }
      return '-';
    },
    variable_test_name(ov) {
      const pd = this.pval_dict;
      if (ov.label in pd) {
        const { test_name } = pd[ov.label];
        return test_name;
      }
      return '-';
    },
    get_variable(ov) {
      const pd = this.pval_dict;
      if (ov.label in pd) {
        return pd[ov.label];
      }
      return null;
    },
    getPvalColor(pv) {
      var c = colors['pvals'][pv.toString() + '-' + this.colorScheme];
      if (c) {
        return c['color'];
      } else {
        return 'white';
      }
    },
    update_table_rows() {
      let pd = {};
      let tr = [];

      if (this.outcomeVariables != null && this.anovaPvals != null) {
        this.anovaPvals.forEach(a => {
          pd[a.label] = a;
        });

        this.outcomeVariables.forEach(ov => {
          // newly-added vars may not appear in the analysis results
          if (!(ov.label in pd)) {
            pd[ov.label] = { fval: 'NA', pval: 'NA' };
          }
          let pv = pd[ov.label];
          tr.push({
            id: ov.id,
            var: ov,
            category: ov.category,
            abbreviation: ov.abbreviation,
            label: ov.label,
            f_statistic: pv.fval,
            p_value: pv.pval,
            error: pv.error,
          });
        });
        this.pval_dict = pd;
        this.tableRows = tr;
        this.autoselectPending = true;
      }
    },
    pval_table_cell_aux(ov, which) {
      const pd = this.pval_dict;
      if (ov.label in pd) {
        const { pval } = pd[ov.label];
        let ccl = this.colors['pvals']['1' + '-' + this.colorScheme][which];
        if (pval < 0.001) {
          ccl = this.colors['pvals']['0.001' + '-' + this.colorScheme][which];
        } else if (pval < 0.01) {
          ccl = this.colors['pvals']['0.01' + '-' + this.colorScheme][which];
        } else if (pval < 0.05) {
          ccl = this.colors['pvals']['0.05' + '-' + this.colorScheme][which];
        } else if (pval < 0.1) {
          ccl = this.colors['pvals']['0.1' + '-' + this.colorScheme][which];
        }
        return ccl;
      }
      return '';
    },
    pval_table_cell_class(ov) {
      return this.pval_table_cell_aux(ov, 'class');
    },
    pval_table_cell_color(ov) {
      return this.pval_table_cell_aux(ov, 'color');
    },
    pval_table_text_color(ov) {
      if (this.colorScheme == 'brewer5') {
        const pd = this.pval_dict;
        if (ov.label in pd) {
          const { pval } = pd[ov.label];
          if (pval < 0.001) {
            return 'white';
          }
        }
      }
      return 'black';
    },
    table_row_click(ov) {
      if (!this.multiSelect) {
        this.$emit('variablesSelected', [ov.var]);
      }
    },
    isOVSelected(ov) {
      return this.selectedVars.includes(ov);
    },
    expandClicked(expand) {
      this.$emit('expandClicked', expand);
    },
    emitSortedSelectedVars(sv) {
      let selIds = {};
      sv.map(v => {
        selIds[v.id] = 1;
      });
      let sortedSelVars = this.sortedTableRows
        .filter(tr => tr.id in selIds)
        .map(tr => tr.var);
      this.$emit('variablesSelected', sortedSelVars);
    },
    // track current sort order
    tableRowsUpdated(nr) {
      this.sortedTableRows = nr;
      if (this.selectedVars.length > 1) {
        this.emitSortedSelectedVars(this.selectedVars);
      } else if (this.autoselectPending && nr.length > 0) {
        this.autoselectPending = false;
        this.$emit('variablesSelected', [nr[0].var]);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
tr.selectedRow {
  background-color: rgb(236, 177, 212);
}

.rowElement {
  display: flex;
  flex-wrap: nowrap;
  min-width: max-content;
}

// the following CSS is not recommended by Vuetify, but it works
// there is another solution, check following if you want to make this code more robust
// https://stackoverflow.com/questions/65688987/vuetify-v-data-table-custom-header-class-styling-not-being-applied

::v-deep .variable-header {
  /* background-color: #DCDCDC;  */
  white-space: nowrap;
}
</style>
